/**
 * Pure formatting / matching helpers for the run-queue UI (fleet strip,
 * queued tile label, run-id matching, durations). No React, no network — the
 * Jest tests cover these directly.
 */

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Normalise the two response shapes that carry fleet data into one model:
 *   GET /rsa/v2/fleet → { last_tick: { fleet, gpu_phase_runs, ... }, policy, queue_totals }
 *   GET /rsa/v2/queue → { fleet, totals, policy, last_tick (ts), waiting_reason }
 */
export const fleetStripModel = (fleetResp, queueResp) => {
  const tick = fleetResp?.last_tick && typeof fleetResp.last_tick === "object"
    ? fleetResp.last_tick
    : null;
  const fleet = tick?.fleet || queueResp?.fleet || null;
  const queued =
    fleetResp?.queue_totals?.pending ??
    queueResp?.totals?.pending ??
    tick?.pending ??
    null;
  const gpuPhaseRuns = tick?.gpu_phase_runs ?? null;
  const activeRuns =
    fleetResp?.queue_totals?.active ?? queueResp?.totals?.active ?? null;
  return {
    hasFleet: Boolean(fleet) && fleet.ok !== false,
    fleetError: fleet?.error || null,
    gpuCapacity: num(fleet?.gpu_capacity),
    gpuRunning: num(fleet?.gpu_running),
    gpuRunnable: num(fleet?.gpu_runnable),
    gpuFree: fleet
      ? num(fleet.gpu_free, Math.max(0, num(fleet.gpu_capacity) - num(fleet.gpu_running) - num(fleet.gpu_runnable)))
      : 0,
    cpuRunning: num(fleet?.cpu_running),
    cpuRunnable: num(fleet?.cpu_runnable),
    // 2026-09-08 contract: instances the fleet is still bringing up (their
    // slots count as free — "Slots free · incl. not yet launched") and the
    // backend's one-line note about that number. Older backends: 0 / null.
    instancesLaunching: num(fleet?.instances_launching),
    slotsFreeNote: fleet?.gpu_slots_free_note ? String(fleet.gpu_slots_free_note) : null,
    gpuPhaseRuns: gpuPhaseRuns == null ? null : num(gpuPhaseRuns),
    activeRuns: activeRuns == null ? null : num(activeRuns),
    queued: queued == null ? null : num(queued),
    lastTick: tick?.ts || queueResp?.last_tick || null,
    waitingReason: tick?.waiting_reason ?? queueResp?.waiting_reason ?? null,
    policy: fleetResp?.policy || queueResp?.policy || null,
  };
};

/** "GPUs 41/90 busy · 12 jobs waiting for an instance · 2 instances launching · 2 runs in GPU phase · queue 4" */
export const fleetStripText = (model) => {
  if (!model) return "Fleet: no data yet";
  const parts = [];
  if (model.hasFleet) {
    parts.push(`GPUs ${model.gpuRunning}/${model.gpuCapacity} busy`);
    parts.push(
      `${model.gpuRunnable} job${model.gpuRunnable === 1 ? "" : "s"} waiting for an instance`,
    );
    if (model.instancesLaunching > 0) {
      parts.push(
        `${model.instancesLaunching} instance${model.instancesLaunching === 1 ? "" : "s"} launching`,
      );
    }
  } else {
    parts.push("GPUs: fleet probe unavailable");
  }
  if (model.gpuPhaseRuns != null) {
    parts.push(
      `${model.gpuPhaseRuns} run${model.gpuPhaseRuns === 1 ? "" : "s"} in GPU phase`,
    );
  }
  if (model.queued != null) {
    parts.push(`queue ${model.queued}`);
  }
  return parts.join(" · ");
};

export const HELD_TILE_LABEL = "Queued · not started";

/** Is this queue entry (or the run's seeded metadata) a HELD entry — queued by an
 *  admin without starting it? */
export const isHeldEntry = (entry, run = null) =>
  String(entry?.status || "").toLowerCase() === "held" ||
  String(run?.metadata?.queue_status || run?.queue?.status || "").toLowerCase() === "held";

/** Tile label for a queued run: "Queued · #3 · ~90 min"; a held entry is
 *  "Queued · not started" (its position is within the held list, not the queue). */
export const queuedTileLabel = (entry) => {
  if (isHeldEntry(entry)) return HELD_TILE_LABEL;
  const parts = ["Queued"];
  const position = Number(entry?.position);
  if (Number.isFinite(position) && position > 0) parts.push(`#${position}`);
  const eta = Number(entry?.eta_minutes);
  if (Number.isFinite(eta) && eta > 0) parts.push(`~${formatMinutes(eta)}`);
  return parts.join(" · ");
};

export const formatMinutes = (minutes) => {
  const m = Math.max(0, Math.round(num(minutes)));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
};

/** Run ids: the queue mints "project_<id>_run_pyexisting_<DDMMYY-HHMM>-<4hex>";
 *  the task list echoes the same string as run_key, but the tile may only hold
 *  the short display id. Match on the whole string first, then on the run token. */
const RUN_TOKEN_RE = /(run_(?:pyexisting_|model_)?\d{6}-\d{4}-[0-9a-f]{4})/i;
export const runToken = (id) => {
  const m = String(id || "").match(RUN_TOKEN_RE);
  return m ? m[1].toLowerCase() : "";
};
export const matchesRunId = (a, b) => {
  const sa = String(a || "").trim();
  const sb = String(b || "").trim();
  if (!sa || !sb) return false;
  if (sa === sb) return true;
  const ta = runToken(sa);
  const tb = runToken(sb);
  if (ta && tb) return ta === tb;
  return sa.endsWith(sb) || sb.endsWith(sa);
};

/** Map every queue entry (held + pending + active + done) by its run_id. */
export const indexQueueByRunId = (queueView) => {
  const out = new Map();
  if (!queueView) return out;
  for (const bucket of ["held", "pending", "active", "done"]) {
    for (const entry of queueView[bucket] || []) {
      if (entry?.run_id) out.set(String(entry.run_id), entry);
    }
  }
  return out;
};

/** Find the queue entry for a dashboard run (run.run_id / full_run_id / run_key). */
export const findQueueEntryForRun = (run, queueIndex) => {
  if (!run || !queueIndex || queueIndex.size === 0) return null;
  const candidates = [run.run_id, run.full_run_id, run.run_key, run.id].filter(Boolean);
  for (const id of candidates) {
    const exact = queueIndex.get(String(id));
    if (exact) return exact;
  }
  for (const [, entry] of queueIndex) {
    if (candidates.some((id) => matchesRunId(id, entry.run_id))) return entry;
  }
  return null;
};

export const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(num(ms) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
};

export const durationBetween = (startIso, endIso, now = Date.now()) => {
  const start = Date.parse(startIso || "");
  if (!Number.isFinite(start)) return null;
  const end = endIso ? Date.parse(endIso) : now;
  if (!Number.isFinite(end)) return null;
  return formatElapsed(end - start);
};

export const relativeTime = (iso, now = Date.now()) => {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "—";
  const diff = Math.max(0, now - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const formatDateTime = (iso) => {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "—";
  return new Date(t).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Outcome label for a history row. */
export const outcomeLabel = (entry) => {
  const status = String(entry?.status || "").toLowerCase();
  switch (status) {
    case "succeeded":
      return "Succeeded";
    case "failed":
      return entry?.execution_status
        ? `Failed (${entry.execution_status})`
        : "Failed";
    case "dispatch_failed":
      return "Dispatch failed";
    case "cancelled":
      return "Cancelled";
    case "admitted":
      return "Running";
    case "queued":
      return "Queued";
    case "held":
      return "Queued (not started)";
    default:
      return status || "—";
  }
};

/** Priority that puts an entry ahead of every other pending entry. */
export const bumpPriorityFor = (entry, pending) => {
  const others = (pending || []).filter((e) => e?.id !== entry?.id);
  const maxOther = others.reduce((m, e) => Math.max(m, num(e?.priority)), 0);
  return Math.max(num(entry?.priority), maxOther) + 1;
};

/**
 * "1,2,3" for a non-empty list of project ids; null otherwise (= no filter).
 * The value of the `project_ids` query parameter that scopes the queue GETs
 * to the projects a non-admin can open (client-enforced, not authorization).
 */
export const projectIdsParam = (projectIds) => {
  if (!Array.isArray(projectIds)) return null;
  const ids = projectIds
    .map((id) => String(id ?? "").trim())
    .filter(Boolean);
  // Sorted + de-duplicated: the same SET of ids always yields the same string,
  // so callers can use it as a change key (useRunQueue does).
  return ids.length ? Array.from(new Set(ids)).sort().join(",") : null;
};

/**
 * Defence in depth for the scoped (non-admin) views: drop every entry whose
 * project is not in `projectIds` from a GET /rsa/v2/queue or /rsa/v2/fleet
 * body, whatever the server sent. A Pipeline backend older than the
 * `project_ids` parameter ignores it and returns the whole queue (PROD did,
 * 2026-09-16: a customer saw another customer's runs). `projectIds` null =
 * admin = untouched; an empty list keeps nothing.
 */
export const scopeToProjects = (body, projectIds) => {
  if (!body || !Array.isArray(projectIds)) return body;
  const allowed = new Set(projectIds.map((id) => String(id ?? "").trim()).filter(Boolean));
  const keep = (list) =>
    Array.isArray(list) ? list.filter((e) => allowed.has(String(e?.project_id ?? "").trim())) : list;
  const scopeTick = (tick) => {
    if (!tick || typeof tick !== "object") return tick;
    const active = keep(tick.active_runs);
    const visible = new Set((active || []).map((a) => String(a?.run_id)));
    const byRun = tick.fleet?.gpu_jobs_by_run;
    const fleet =
      byRun && typeof byRun === "object"
        ? {
            ...tick.fleet,
            gpu_jobs_by_run: Object.fromEntries(Object.entries(byRun).filter(([runId]) => visible.has(String(runId)))),
          }
        : tick.fleet;
    return { ...tick, active_runs: active, fleet };
  };
  const out = { ...body };
  for (const key of ["held", "pending", "active", "done"]) {
    if (key in out) out[key] = keep(out[key]);
  }
  if ("last_tick" in out) out.last_tick = scopeTick(out.last_tick);
  if (out.fleet?.gpu_jobs_by_run) out.fleet = scopeTick({ active_runs: [...(out.active || [])], fleet: out.fleet }).fleet;
  return out;
};
